import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { Position, useUpdateNodeInternals, useZoomPanHelper } from 'react-flow-renderer';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { StartDialogueActionModel, TreeEnterActionModel, TreeParamterActionModel, TreePropertiesActionModel, DebugStartActionModel } from '../../../shared/action/ActionModel';
import { ActionTreeModel, subTreeNodeSize } from '../../../shared/action/ActionTreeModel';
import { doScale, doScaleNumber } from './actionEditorHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BsZoomIn } from 'react-icons/bs';
import { UiConstants } from '../../data/UiConstants';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { Gender } from '../../../shared/definitions/other/Gender';
import { ActionNodeDescription, ActionNodeHeader } from "./ActionNodeHeader";
import { ActionNodeHandle } from './handle/ActionNodeHandle';
import { AiOutlineDrag } from 'react-icons/ai';
import { undoableActionEditorSelectAction } from '../../stores/undo/operation/ActionEditorSelectActionOp';
import { passComplexityGate } from '../../../shared/definitions/other/EditorComplexity';
import { actionEditorStore } from '../../stores/ActionEditorStore';
import { gameStore } from '../../stores/GameStore';
import { localSettingsStore } from '../../stores/LocalSettingsStore';
import { getActionShortDescriptionForActionEditor } from '../../helper/actionEditorHelpers';
import { actionNodeIcon } from './actionNodeData'; // Make all FA solid icons available so that they can be individually configured for sub-tree (template) nodes

library.add(fas);
const ActionNodeContent = styled.div.withConfig({
  displayName: "ActionNode__ActionNodeContent",
  componentId: "sc-1hbhvwn-0"
})(["outline:", ";background-color:", ";border:", " solid ", ";box-sizing:border-box;white-space:nowrap;font-size:", ";padding-top:", ";padding-bottom:", ";padding-left:", ";border-radius:", ";box-shadow:0 ", " ", " 0 rgba(0,0,0,0.2);button{background-color:", ";}"], props => props.invalid ? css(["0.15em solid red"]) : undefined, props => props.greyedOut ? props.color + "55" : props.color, props => doScale(1, props.scale), props => props.color, props => doScale(11, props.scale), props => doScale(5, props.scale), props => doScale(5, props.scale), props => doScale(5, props.scale), props => doScale(4, props.scale), props => doScale(4, props.scale), props => doScale(8, props.scale), props => props.color ? props.color : 'unset');
const TreeActionNodeContent = styled(ActionNodeContent).withConfig({
  displayName: "ActionNode__TreeActionNodeContent",
  componentId: "sc-1hbhvwn-1"
})(["width:", ";min-height:", ";padding-left:0px;"], props => doScale(subTreeNodeSize.width, props.scale), props => doScale(subTreeNodeSize.height, props.scale));
const TreeActionNodeEntries = styled.div.withConfig({
  displayName: "ActionNode__TreeActionNodeEntries",
  componentId: "sc-1hbhvwn-2"
})(["padding-right:", ";"], props => doScale(30, props.scale));
const TreeActionNodeExits = styled.div.withConfig({
  displayName: "ActionNode__TreeActionNodeExits",
  componentId: "sc-1hbhvwn-3"
})(["padding-left:", ";"], props => doScale(60, props.scale));
const TreeActionNodeParameters = styled.div.withConfig({
  displayName: "ActionNode__TreeActionNodeParameters",
  componentId: "sc-1hbhvwn-4"
})(["padding-left:", ";"], props => doScale(30, props.scale));
const Button = styled.button.withConfig({
  displayName: "ActionNode__Button",
  componentId: "sc-1hbhvwn-5"
})(["position:absolute;top:-2.7em;width:2.7em;height:2.7em;outline:none;border:none;border-radius:100em 100em 0em 0em;border-bottom:0.04em solid rgba(0,0,0,10%);"]);
const FocusButton = styled(Button).withConfig({
  displayName: "ActionNode__FocusButton",
  componentId: "sc-1hbhvwn-6"
})(["right:3.6em;cursor:pointer;"]);
const DragHandle = styled(Button).withConfig({
  displayName: "ActionNode__DragHandle",
  componentId: "sc-1hbhvwn-7"
})(["right:0.3em;cursor:grab;"]);
const ActionNodeParameterLabel = styled.div.withConfig({
  displayName: "ActionNode__ActionNodeParameterLabel",
  componentId: "sc-1hbhvwn-8"
})(["background:", ";color:#444;text-align:", ";display:inline-block;width:100%;margin-bottom:", ";height:", ";max-width:", ";overflow:hidden;text-overflow:ellipsis;padding-left:", ";padding-right:", ";border-top-left-radius:", ";border-bottom-left-radius:", ";border-top-right-radius:", ";border-bottom-right-radius:", ";"], props => props.conditionSet ? '#8f5252' : 'rgba(255, 255, 255, 0.5)', props => props.align, props => doScale(0.23, props.scale), props => doScale(13, props.scale), props => doScale(140, props.scale), props => doScale(2, props.scale), props => doScale(2, props.scale), props => props.align == 'right' ? doScale(100, props.scale) : '0', props => props.align == 'right' ? doScale(100, props.scale) : '0', props => props.align == 'left' ? doScale(100, props.scale) : '0', props => props.align == 'left' ? doScale(100, props.scale) : '0');
const IconBubble = styled.div.withConfig({
  displayName: "ActionNode__IconBubble",
  componentId: "sc-1hbhvwn-9"
})(["position:absolute;background-color:", ";border-radius:50% 50% 0px 50%;width:", ";height:", ";display:flex;justify-content:center;align-items:center;transform:translate(-", ",-", ");"], props => props.color ? props.color : 'white', props => doScale(20, props.scale), props => doScale(20, props.scale), props => doScale(17, props.scale), props => doScale(18, props.scale));
const SubTreeIconBubble = styled(IconBubble).withConfig({
  displayName: "ActionNode__SubTreeIconBubble",
  componentId: "sc-1hbhvwn-10"
})(["transform:translate(-", ",-", ");"], props => doScale(14, props.scale), props => doScale(18, props.scale));
const selectMargin = 6;
const SelectionBorder = styled.div.withConfig({
  displayName: "ActionNode__SelectionBorder",
  componentId: "sc-1hbhvwn-11"
})(["position:absolute;width:calc(100% + ", ");height:calc(100% + ", ");border-color:", ";border-style:dashed;border-width:", ";margin-top:-", ";margin-left:-", ";pointer-events:none;border-radius:", ";"], props => doScale(selectMargin * 2, props.scale), props => doScale(selectMargin * 2, props.scale), UiConstants.COLOR_SELECTION_HIGHLIGHT, props => doScale(2, props.scale), props => doScale(selectMargin, props.scale), props => doScale(selectMargin, props.scale), props => doScale(4, props.scale));
const ActiveNodeBorder = styled(SelectionBorder).withConfig({
  displayName: "ActionNode__ActiveNodeBorder",
  componentId: "sc-1hbhvwn-12"
})(["border-color:", ";"], UiConstants.COLOR_ACTIVE);
export const ActionNode = observer(({
  data,
  xPos,
  yPos
}) => {
  var _subTree$treeProperti, _subTree$treeProperti2;

  const {
    t
  } = useTranslation();
  const {
    languageKey
  } = gameStore;
  const {
    currentRootActionTree,
    currentActionSubTreeModelId,
    currentActionTreeHierarchy,
    currentAction,
    currentActionSubTree
  } = actionEditorStore;
  const {
    action,
    scale
  } = data;
  const nodeId = data.action.$modelId;
  const bounds = {
    x: xPos,
    y: yPos,
    width: doScaleNumber(subTreeNodeSize.width, scale),
    height: doScaleNumber(subTreeNodeSize.height, scale)
  };
  const {
    actionTreeValidationEnabled
  } = localSettingsStore;
  const exits = action.exits();
  const exitLabels = action.exitLabels(t, gameStore.languageKey);
  const description = getActionShortDescriptionForActionEditor(action, t);
  const subTree = action instanceof ActionTreeModel ? action : null;
  const subTreeName = subTree && action != currentRootActionTree ? subTree === null || subTree === void 0 ? void 0 : (_subTree$treeProperti = subTree.treePropertiesAction) === null || _subTree$treeProperti === void 0 ? void 0 : _subTree$treeProperti.localizedName.get(gameStore.languageKey) : "";
  const subTreeProps = subTree === null || subTree === void 0 ? void 0 : subTree.treePropertiesAction;
  const subTreeEntries = subTree === null || subTree === void 0 ? void 0 : subTree.enterActions;
  const isNodeWithoutEntry = action instanceof TreeEnterActionModel || action instanceof TreePropertiesActionModel || action instanceof TreeParamterActionModel || action instanceof DebugStartActionModel;
  const isDialogueWithAnswers = action instanceof StartDialogueActionModel && action.answers.length > 0;
  const isSelected = currentAction === action;
  const isRootNode = action.$modelId == (currentRootActionTree === null || currentRootActionTree === void 0 ? void 0 : currentRootActionTree.$modelId);
  const markAsInvalid = actionTreeValidationEnabled && actionEditorStore.shouldMarkAsInvalid(action);

  const isSubTreeInFocus = () => {
    return currentActionSubTreeModelId === action.$modelId;
  };

  const isSubTreeInHierarchy = () => {
    return currentActionTreeHierarchy.includes(subTree);
  };

  const markAsDisconnected = actionTreeValidationEnabled && !isSubTreeInHierarchy() && actionEditorStore.shouldMarkAsDisconnected(data.action, currentActionSubTree);

  const subTreeBackgroundColor = (forIcon = false) => {
    const definedColor = subTreeProps === null || subTreeProps === void 0 ? void 0 : subTreeProps.color;

    if (isSubTreeInFocus()) {
      return definedColor && forIcon ? definedColor : 'white'; // When Subtree is in focus
    }

    for (const aNode of currentActionTreeHierarchy) {
      if (aNode.$modelId === action.$modelId) {
        return '#cccccc'; // When Subtree is part of hierarchy but not in focus
      }
    }

    return definedColor ? definedColor : subTree.nodeColor(); // When Subtree is not part of the hierarchy
  };

  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(action.$modelId);
  }, [exits.length]);
  const color = subTree ? subTreeBackgroundColor() : action.nodeColor();

  const createHandleStyle = (position, markAsDisconnected, yPos = 24) => {
    const handleSize = doScale(13, scale);
    const handlerPosX = doScale(14.25, scale);
    const handlerPosY = doScale(yPos, scale); //const borderRadius = doScale(4, scale);

    const borderRadius = "100%";
    const style = {
      top: handlerPosY,
      width: handleSize,
      height: handleSize,
      border: doScale(1, scale) + " solid " + color,
      borderRadius: 0,
      backgroundColor: color
    };

    if (position === Position.Left) {
      style.left = "-" + handlerPosX;
      style.borderBottomLeftRadius = borderRadius;
      style.borderTopLeftRadius = borderRadius;
    }

    if (position === Position.Right) {
      style.right = "-" + handlerPosX;
      style.borderBottomRightRadius = borderRadius;
      style.borderTopRightRadius = borderRadius;
    }

    if (markAsDisconnected) {
      style.backgroundColor = "white";
    }

    return style;
  };

  const calcHandleYPosition = handleIndex => {
    return handleIndex * 15.942 + 48.48;
  };

  const exitHasCondition = exit => {
    return exit.hideCondition && exit.hideCondition.variableName !== "";
  };

  const zoomPanHelper = useZoomPanHelper();

  const focus = e => {
    e.stopPropagation();
    zoomPanHelper.fitBounds(bounds, 0.2);
  };

  const onClickOnTreeActionNode = () => {
    undoableActionEditorSelectAction(action);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, isSelected && /*#__PURE__*/React.createElement(SelectionBorder, {
    scale: scale
  }), action.$modelId === gameStore.debugStartNodeModelId && /*#__PURE__*/React.createElement(ActiveNodeBorder, {
    scale: scale
  }), // == ACTION TREE MODEL ==
  subTree && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TreeActionNodeContent, {
    greyedOut: markAsDisconnected,
    scale: scale,
    selected: isSelected,
    invalid: markAsInvalid,
    color: color,
    onClick: onClickOnTreeActionNode
  }, !isSubTreeInHierarchy() && /*#__PURE__*/React.createElement(React.Fragment, null, subTreeProps && passComplexityGate(subTreeProps.complexityGate, localSettingsStore.editorComplexity) && /*#__PURE__*/React.createElement(FocusButton, {
    onClickCapture: focus
  }, /*#__PURE__*/React.createElement(BsZoomIn, {
    size: "1.5em"
  })), /*#__PURE__*/React.createElement(DragHandle, {
    className: "dragHandle"
  }, /*#__PURE__*/React.createElement(AiOutlineDrag, {
    size: "1.5em"
  }))), !isRootNode && /*#__PURE__*/React.createElement(SubTreeIconBubble, {
    scale: scale,
    color: color,
    greyedOut: markAsDisconnected,
    selected: isSelected
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: subTreeProps !== null && subTreeProps !== void 0 && subTreeProps.icon ? subTreeProps.icon : "align-justify"
  })), !isSubTreeInHierarchy() && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ActionNodeHeader, {
    title: subTreeName,
    scale: scale
  }), /*#__PURE__*/React.createElement(TreeActionNodeEntries, {
    scale: scale
  }, subTree.enterActions.map((entry, index) => /*#__PURE__*/React.createElement("div", {
    key: index
  }, /*#__PURE__*/React.createElement(ActionNodeParameterLabel, {
    scale: scale,
    align: "left"
  }, entry.name.get(languageKey))))), /*#__PURE__*/React.createElement(TreeActionNodeExits, {
    scale: scale
  }, subTree.exits().map((_, index) => /*#__PURE__*/React.createElement("div", {
    key: index
  }, /*#__PURE__*/React.createElement(ActionNodeParameterLabel, {
    scale: scale,
    align: "right"
  }, exitLabels[index])))), /*#__PURE__*/React.createElement(TreeActionNodeParameters, {
    scale: scale
  }, subTree.treeParameterActions().filter(p => p.showOnNode).map((p, index) => /*#__PURE__*/React.createElement("div", {
    key: index
  }, /*#__PURE__*/React.createElement(ActionNodeParameterLabel, {
    scale: scale,
    align: "center"
  }, p.name, " = ", p.value.get(languageKey, Gender.Neutral))))), /*#__PURE__*/React.createElement(ActionNodeDescription, {
    scale: scale,
    description: (_subTree$treeProperti2 = subTree.treePropertiesAction) === null || _subTree$treeProperti2 === void 0 ? void 0 : _subTree$treeProperti2.description.get(languageKey)
  }))), // enter sockets
  !isRootNode && subTreeEntries.map((enterModel, index) => /*#__PURE__*/React.createElement(ActionNodeHandle, {
    key: index,
    type: "target",
    id: "f" + index,
    position: Position.Left,
    style: createHandleStyle(Position.Left, actionTreeValidationEnabled && !actionEditorStore.isInputSocketConnected(enterModel, currentActionSubTree.allActions), calcHandleYPosition(index)),
    nodeId: nodeId
  })), // exit sockets
  !isRootNode && subTree.exits().map((exitModel, index) => /*#__PURE__*/React.createElement(ActionNodeHandle, {
    key: index,
    type: "source",
    id: "f" + index,
    position: Position.Right,
    style: createHandleStyle(Position.Right, actionTreeValidationEnabled && !exitModel.hasNextActions(), calcHandleYPosition(subTreeEntries.length + index)),
    nodeId: nodeId
  }))), // == ACTION MODEL ==
  !subTree && /*#__PURE__*/React.createElement(ActionNodeContent, {
    greyedOut: markAsDisconnected,
    scale: scale,
    selected: isSelected,
    invalid: markAsInvalid,
    color: color,
    className: "dragHandle"
  }, actionNodeIcon(action) && /*#__PURE__*/React.createElement(IconBubble, {
    scale: scale,
    color: color,
    selected: isSelected,
    greyedOut: markAsDisconnected
  }, actionNodeIcon(action)), /*#__PURE__*/React.createElement(ActionNodeHeader, {
    title: t(action.title()),
    description: description,
    scale: scale
  }), !isNodeWithoutEntry && /*#__PURE__*/React.createElement(ActionNodeHandle, {
    type: "target",
    position: Position.Left,
    id: "f",
    style: createHandleStyle(Position.Left, actionTreeValidationEnabled && !actionEditorStore.isInputSocketConnected(action, currentActionSubTree.allActions)),
    nodeId: nodeId
  }), exits.length == 1 && !isDialogueWithAnswers && /*#__PURE__*/React.createElement(ActionNodeHandle, {
    type: "source",
    id: "f0",
    position: Position.Right,
    style: createHandleStyle(Position.Right, actionTreeValidationEnabled && !exits[0].hasNextActions()),
    nodeId: nodeId
  }), (exits.length > 1 || isDialogueWithAnswers) && /*#__PURE__*/React.createElement(React.Fragment, null, exits.map((exit, index) => /*#__PURE__*/React.createElement("div", {
    key: index
  }, exitLabels && /*#__PURE__*/React.createElement(ActionNodeParameterLabel, {
    scale: scale,
    align: "right"
  }, exitLabels[index]), !exitLabels && /*#__PURE__*/React.createElement(ActionNodeParameterLabel, {
    conditionSet: exitHasCondition(exit),
    scale: scale,
    align: "right"
  }, exit.value.get(languageKey)), /*#__PURE__*/React.createElement(ActionNodeHandle, {
    key: index,
    type: "source",
    id: "f" + index,
    position: Position.Right,
    style: createHandleStyle(Position.Right, actionTreeValidationEnabled && !exit.hasNextActions(), calcHandleYPosition(index)),
    nodeId: nodeId
  }))))));
});